import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Settings, Moon, Sun, ChevronDown, User, LogOut, LineChart, Users, Home, Zap } from 'lucide-react';
import {
  INTELLIGENCE_DROPDOWN,
  INTELLIGENCE_NAV_ITEMS,
  PLATFORM_NAV_ITEMS,
  PREDICTION_DROPDOWN,
  PREDICTION_NAV_ITEMS,
  isPlatformWorkspacePage,
} from '../config/platformNav';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useCorporateNav } from '../context/CorporateNavContext';
import { usePolicyMakerNav } from '../context/PolicyMakerNavContext';
import { getVisibleCorporateTabs } from '../utils/corporatePageAccess';
import { getVisiblePolicyMakerRoutes } from '../utils/policyMakerPageAccess';
import { canAccessPath, firstAccessiblePath } from '../utils/featureAccess';
import GlobalSearch from './GlobalSearch';
import PolicyMakerPlatformNav from './PolicyMakerNav';

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const { user, logout, isSuperAdmin } = useAuth();
  const corporateNav = useCorporateNav();
  const corporateWorkspaceNav = corporateNav?.workspaceNav;
  const policyMakerNav = usePolicyMakerNav();
  const policyWorkspaceNav = policyMakerNav?.workspaceNav;
  // On /account-settings, mirror the user's own workspace nav (Corporate / Policy Maker)
  const isAccountSettingsRoute = location.pathname === '/account-settings';
  const mirrorCorporateNavOnAccountSettings =
    isAccountSettingsRoute && !isSuperAdmin && user?.registration_type === 'corporate';
  const mirrorPolicyMakerNavOnAccountSettings =
    isAccountSettingsRoute && !isSuperAdmin && user?.registration_type === 'policy_maker';
  const isCorporateWorkspaceRoute =
    location.pathname.startsWith('/corporate/') || mirrorCorporateNavOnAccountSettings;
  const isPolicyMakersRoute =
    location.pathname.startsWith('/policy-makers') || mirrorPolicyMakerNavOnAccountSettings;
  const isPolicyMakerWorkspace = isPolicyMakersRoute || Boolean(policyWorkspaceNav);
  const [isPredictionOpen, setIsPredictionOpen] = useState(false);
  const [isIntelligenceOpen, setIsIntelligenceOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileIntelligenceOpen, setIsMobileIntelligenceOpen] = useState(false);
  const [isMobilePredictionOpen, setIsMobilePredictionOpen] = useState(false);
  const predictionDropdownRef = useRef(null);
  const intelligenceDropdownRef = useRef(null);
  const userMenuRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (predictionDropdownRef.current && !predictionDropdownRef.current.contains(event.target)) {
        setIsPredictionOpen(false);
      }
      if (intelligenceDropdownRef.current && !intelligenceDropdownRef.current.contains(event.target)) {
        setIsIntelligenceOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    setIsUserMenuOpen(false);
    logout();
    navigate('/login');
  };

  const handleContextSwitch = () => {
    setIsUserMenuOpen(false);
    closeMobileMenu();
    if (isEVDashboardMode) {
      navigate(firstAccessiblePath(features, user));
      return;
    }
    navigate('/ev-dashboard');
  };

  const goToUserManagement = () => {
    setIsUserMenuOpen(false);
    closeMobileMenu();
    navigate('/admin/users');
  };

  // Check if we're on EV dashboard pages (or EV-context search).
  const searchContext = new URLSearchParams(location.search).get('context');
  const isEVDashboardMode =
    location.pathname === '/ev-dashboard' ||
    location.pathname === '/ev_crisis_simulator' ||
    (location.pathname === '/search' && searchContext === 'ev');
  const isMinimalNavPage =
    !isSuperAdmin &&
    (location.pathname === '/' ||
      location.pathname === '/corporate' ||
      isCorporateWorkspaceRoute ||
      location.pathname === '/policy-maker' ||
      isPolicyMakersRoute);
  const showPlatformWorkspaceNav =
    !isCorporateWorkspaceRoute &&
    !isEVDashboardMode &&
    !isPolicyMakersRoute &&
    (isPolicyMakerWorkspace ||
      isPlatformWorkspacePage(location.pathname) ||
      (isSuperAdmin && location.pathname === '/'));
  const features = user?.subscription_plan_features || [];

  const visibleNavItems = PLATFORM_NAV_ITEMS.filter((item) =>
    canAccessPath(item.path, features, user),
  );

  const visibleIntelligenceItems = INTELLIGENCE_NAV_ITEMS.filter((item) =>
    canAccessPath(item.path, features, user),
  );
  const isIntelligenceActive = INTELLIGENCE_NAV_ITEMS.some(
    (item) => item.path === location.pathname && canAccessPath(item.path, features, user),
  );

  // EV Dashboard navigation items (gated like critical-minerals pages)
  const evNavItems = [
    { path: '/ev-dashboard', label: 'EV Dashboard', icon: Home },
    { path: '/ev_crisis_simulator', label: 'Crisis Simulator', icon: Zap },
  ];
  const visibleEvNavItems = evNavItems.filter((item) => canAccessPath(item.path, features, user));

  const visiblePredictionItems = PREDICTION_NAV_ITEMS.filter((item) =>
    canAccessPath(item.path, features, user),
  );
  const isPredictionActive = PREDICTION_NAV_ITEMS.some(
    (item) => item.path === location.pathname && canAccessPath(item.path, features, user),
  );
  const intelligenceBriefingNavItem = INTELLIGENCE_NAV_ITEMS.find(
    (item) => item.path === '/intelligence-briefing',
  );
  const showIntelligenceBriefingNav =
    intelligenceBriefingNavItem &&
    canAccessPath(intelligenceBriefingNavItem.path, features, user);
  const workspaceTabLinkClass = (isActive) =>
    `inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap shrink-0 transition-colors ${
      isActive
        ? 'bg-[#03396c]/10 dark:bg-blue-900/40 text-[#03396c] dark:text-blue-300 border border-[#03396c]/30 dark:border-blue-700'
        : 'text-gray-600 dark:text-gray-400 border border-transparent hover:bg-gray-100 dark:hover:bg-gray-700'
    }`;
  const workspaceMobileLinkClass = (isActive) =>
    `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${
      isActive
        ? 'bg-[#03396c]/10 dark:bg-blue-900/40 text-[#03396c] dark:text-blue-300'
        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
    }`;
  const renderIntelligenceBriefingNavLink = (linkClass, onClick) => {
    if (!showIntelligenceBriefingNav) return null;
    const Icon = intelligenceBriefingNavItem.icon;
    const isActive = location.pathname === intelligenceBriefingNavItem.path;
    return (
      <Link
        key={intelligenceBriefingNavItem.path}
        to={intelligenceBriefingNavItem.path}
        onClick={onClick}
        className={linkClass(isActive)}
        title={intelligenceBriefingNavItem.label}
      >
        <Icon className="h-3.5 w-3.5 shrink-0" />
        <span className="hidden sm:inline">{intelligenceBriefingNavItem.label}</span>
        <span className="sm:hidden">Briefing</span>
      </Link>
    );
  };
  const renderIntelligenceBriefingMobileLink = (onClick) => {
    if (!showIntelligenceBriefingNav) return null;
    const Icon = intelligenceBriefingNavItem.icon;
    const isActive = location.pathname === intelligenceBriefingNavItem.path;
    return (
      <Link
        key={`mobile-${intelligenceBriefingNavItem.path}`}
        to={intelligenceBriefingNavItem.path}
        onClick={onClick}
        className={workspaceMobileLinkClass(isActive)}
      >
        <Icon className="h-4 w-4" />
        <span>{intelligenceBriefingNavItem.label}</span>
      </Link>
    );
  };
  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setIsMobileIntelligenceOpen(false);
    setIsMobilePredictionOpen(false);
  };

  useEffect(() => {
    closeMobileMenu();
  }, [location.pathname]);

  const mobileUserActions = (
    <div className="pt-2 mt-2 border-t border-secondary-200 dark:border-gray-700 space-y-1">
      {isSuperAdmin && (
        <button
          type="button"
          onClick={goToUserManagement}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-left text-secondary-600 dark:text-gray-300 hover:bg-secondary-100 dark:hover:bg-gray-700"
        >
          <Users className="h-4 w-4" />
          <span>User Management</span>
        </button>
      )}
      {isSuperAdmin && (
        <div
          className="border-t border-secondary-200 dark:border-gray-600 my-1 mx-3"
          role="separator"
        />
      )}
      <Link
        to="/account-settings"
        onClick={closeMobileMenu}
        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-secondary-600 dark:text-gray-300 hover:bg-secondary-100 dark:hover:bg-gray-700"
      >
        <Settings className="h-4 w-4" />
        <span>User settings</span>
      </Link>
      {isSuperAdmin && (
        <button
          type="button"
          onClick={handleContextSwitch}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-secondary-600 dark:text-gray-300 hover:bg-secondary-100 dark:hover:bg-gray-700"
        >
          <LineChart className="h-4 w-4" />
          <span>
            {isEVDashboardMode ? 'Switch to Critical Minerals' : 'Switch to Electric Vehicles'}
          </span>
        </button>
      )}
      <button
        type="button"
        onClick={() => toggleDarkMode()}
        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-secondary-600 dark:text-gray-300 hover:bg-secondary-100 dark:hover:bg-gray-700"
      >
        {isDarkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        <span>{isDarkMode ? 'Light mode' : 'Dark mode'}</span>
      </button>
      <button
        type="button"
        onClick={() => {
          closeMobileMenu();
          handleLogout();
        }}
        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-red-600 dark:text-red-400 hover:bg-secondary-100 dark:hover:bg-gray-700"
      >
        <LogOut className="h-4 w-4" />
        <span>Log out</span>
      </button>
    </div>
  );

  const renderHamburgerButton = () => (
    <button
      type="button"
      onClick={() => setIsMobileMenuOpen((prev) => !prev)}
      className="p-2 rounded-lg text-secondary-600 dark:text-gray-300 hover:bg-secondary-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-[#03396c]"
      aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
      aria-expanded={isMobileMenuOpen}
    >
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        {isMobileMenuOpen ? (
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        ) : (
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 6h16M4 12h16M4 18h16"
          />
        )}
      </svg>
    </button>
  );

  const userMenu = (
    <div className="relative" ref={userMenuRef}>
      <button
        type="button"
        onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
        className="flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium text-secondary-600 dark:text-gray-300 hover:bg-secondary-100 dark:hover:bg-gray-700"
      >
        <User className="h-4 w-4" />
        <span className="max-w-[120px] truncate">{user?.full_name || user?.username || 'User'}</span>
        <ChevronDown className={`h-3 w-3 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`} />
      </button>
      {isUserMenuOpen && (
        <div
          className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 py-1"
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          role="presentation"
        >
          {isSuperAdmin && (
            <button
              type="button"
              onClick={goToUserManagement}
              className="w-full flex items-center space-x-3 px-4 py-2 text-sm font-medium text-left text-secondary-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <Users className="h-4 w-4" />
              <span>User Management</span>
            </button>
          )}
          {isSuperAdmin && (
            <div className="border-t border-gray-200 dark:border-gray-600 my-1" role="separator" />
          )}
          <Link
            to="/account-settings"
            onClick={() => setIsUserMenuOpen(false)}
            className="flex items-center space-x-3 px-4 py-2 text-sm font-medium text-secondary-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <Settings className="h-4 w-4" />
            <span>User settings</span>
          </Link>
          {isSuperAdmin && (
            <button
              type="button"
              onClick={handleContextSwitch}
              className="flex items-center space-x-3 px-4 py-2 w-full text-left text-sm font-medium text-secondary-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <LineChart className="h-4 w-4" />
              <span>{isEVDashboardMode ? 'Switch to Critical Minerals' : 'Switch to Electric Vehicles'}</span>
            </button>
          )}
          <button
            type="button"
            onClick={() => { toggleDarkMode(); }}
            className="flex items-center space-x-3 px-4 py-2 w-full text-left text-sm font-medium text-secondary-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            {isDarkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            <span>{isDarkMode ? 'Light mode' : 'Dark mode'}</span>
          </button>
          <button
            type="button"
            onClick={handleLogout}
            className="flex items-center space-x-3 px-4 py-2 w-full text-left text-sm font-medium text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <LogOut className="h-4 w-4" />
            <span>Log out</span>
          </button>
        </div>
      )}
    </div>
  );

  if (isCorporateWorkspaceRoute) {
    const visibleCorporateTabs = getVisibleCorporateTabs(features, user);
    const onEditProfile = corporateWorkspaceNav?.onEditProfile;

    return (
      <nav className="sticky top-0 z-50 bg-white dark:bg-gray-800 border-b border-secondary-200 dark:border-gray-700 transition-colors duration-200 w-full">
        <div className="w-full px-4 sm:px-6">
          {isSuperAdmin && (
            <div className="hidden md:flex items-center justify-end gap-2 h-10 border-b border-gray-200 dark:border-gray-700">
              {userMenu}
            </div>
          )}

          <div className="flex items-center gap-3 min-h-[52px] py-1">
            <Link to="/corporate/alerts" className="flex items-center flex-shrink-0">
              <img
                src={isDarkMode ? '/images/logo_dark.svg' : '/images/logo.svg'}
                alt="Stratosphere Logo"
                className="h-10 w-auto object-contain"
              />
            </Link>
            <div className="hidden md:block flex-1 min-w-0 overflow-x-auto overscroll-x-contain [-webkit-overflow-scrolling:touch]">
              <div className="flex items-center gap-1 py-1 min-w-max">
                {visibleCorporateTabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = location.pathname === tab.path;
                  return (
                    <Link
                      key={tab.path}
                      to={tab.path}
                      className={workspaceTabLinkClass(isActive)}
                      title={tab.label}
                    >
                      <Icon className="h-3.5 w-3.5 shrink-0" />
                      <span className="hidden sm:inline">{tab.label}</span>
                      <span className="sm:hidden">{tab.shortLabel}</span>
                    </Link>
                  );
                })}
                {renderIntelligenceBriefingNavLink(workspaceTabLinkClass)}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0 pl-2 ml-auto md:ml-0">
              {onEditProfile && (
                <button
                  type="button"
                  onClick={onEditProfile}
                  className="hidden sm:flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 shrink-0"
                  title="Edit corporate profile"
                >
                  <Settings className="h-4 w-4" />
                  <span>Settings</span>
                </button>
              )}
              <div className="md:hidden">{renderHamburgerButton()}</div>
              {!isSuperAdmin && <div className="hidden md:block">{userMenu}</div>}
            </div>
          </div>

          {isMobileMenuOpen && (
            <div className="md:hidden pb-4 border-t border-gray-200 dark:border-gray-700">
              <div className="pt-3 space-y-1 max-h-[75vh] overflow-y-auto">
                {visibleCorporateTabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = location.pathname === tab.path;
                  return (
                    <Link
                      key={`corporate-mobile-${tab.path}`}
                      to={tab.path}
                      onClick={closeMobileMenu}
                      className={workspaceMobileLinkClass(isActive)}
                    >
                      <Icon className="h-4 w-4" />
                      <span>{tab.label}</span>
                    </Link>
                  );
                })}
                {renderIntelligenceBriefingMobileLink(closeMobileMenu)}
                {onEditProfile && (
                  <button
                    type="button"
                    onClick={() => {
                      closeMobileMenu();
                      onEditProfile();
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <Settings className="h-4 w-4" />
                    <span>Edit corporate profile</span>
                  </button>
                )}
                {mobileUserActions}
              </div>
            </div>
          )}
        </div>
      </nav>
    );
  }

  if (isPolicyMakersRoute) {
    const visiblePolicyRoutes = getVisiblePolicyMakerRoutes(features, user);
    const onEditProfile = policyWorkspaceNav?.onEditProfile;

    return (
      <nav className="sticky top-0 z-50 bg-white dark:bg-gray-800 border-b border-secondary-200 dark:border-gray-700 transition-colors duration-200 w-full">
        <div className="w-full px-4 sm:px-6">
          {isSuperAdmin && (
            <div className="hidden md:flex items-center justify-end gap-2 h-10 border-b border-gray-200 dark:border-gray-700">
              {userMenu}
            </div>
          )}
          <div className="flex items-center gap-3 min-h-[52px] py-1">
            <Link to="/policy-makers/alerts" className="flex items-center flex-shrink-0">
              <img
                src={isDarkMode ? '/images/logo_dark.svg' : '/images/logo.svg'}
                alt="Stratosphere Logo"
                className="h-10 w-auto object-contain"
              />
            </Link>
            <div className="hidden md:block flex-1 min-w-0 overflow-x-auto overscroll-x-contain [-webkit-overflow-scrolling:touch]">
              <div className="flex items-center gap-1 py-1 min-w-max">
                {visiblePolicyRoutes.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = location.pathname === tab.path;
                  return (
                    <Link
                      key={tab.path}
                      to={tab.path}
                      className={workspaceTabLinkClass(isActive)}
                      title={tab.label}
                    >
                      <Icon className="h-3.5 w-3.5 shrink-0" />
                      <span className="hidden sm:inline">{tab.label}</span>
                      <span className="sm:hidden">{tab.shortLabel}</span>
                    </Link>
                  );
                })}
                {renderIntelligenceBriefingNavLink(workspaceTabLinkClass)}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0 pl-2 ml-auto md:ml-0">
              {onEditProfile && (
                <button
                  type="button"
                  onClick={onEditProfile}
                  className="hidden sm:flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 shrink-0"
                  title="Edit policy maker profile"
                >
                  <Settings className="h-4 w-4" />
                  <span>Settings</span>
                </button>
              )}
              <div className="md:hidden">{renderHamburgerButton()}</div>
              {!isSuperAdmin && <div className="hidden md:block">{userMenu}</div>}
            </div>
          </div>

          {isMobileMenuOpen && (
            <div className="md:hidden pb-4 border-t border-gray-200 dark:border-gray-700">
              <div className="pt-3 space-y-1 max-h-[75vh] overflow-y-auto">
                {visiblePolicyRoutes.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = location.pathname === tab.path;
                  return (
                    <Link
                      key={`policy-mobile-${tab.path}`}
                      to={tab.path}
                      onClick={closeMobileMenu}
                      className={workspaceMobileLinkClass(isActive)}
                    >
                      <Icon className="h-4 w-4" />
                      <span>{tab.label}</span>
                    </Link>
                  );
                })}
                {renderIntelligenceBriefingMobileLink(closeMobileMenu)}
                {onEditProfile && (
                  <button
                    type="button"
                    onClick={() => {
                      closeMobileMenu();
                      onEditProfile();
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <Settings className="h-4 w-4" />
                    <span>Edit policy profile</span>
                  </button>
                )}
                {mobileUserActions}
              </div>
            </div>
          )}
        </div>
      </nav>
    );
  }

  if (showPlatformWorkspaceNav) {
    const onEditProfile = policyWorkspaceNav?.onEditProfile;
    const platformNavItems = visibleNavItems.filter((item) => item.path !== '/');
    const mobilePlatformLinks = [
      ...platformNavItems,
      ...visibleIntelligenceItems,
      ...visiblePredictionItems,
    ];

    return (
      <nav className="sticky top-0 z-50 bg-white dark:bg-gray-800 border-b border-secondary-200 dark:border-gray-700 transition-colors duration-200 w-full">
        <div className="w-full px-4 sm:px-6">
          {isSuperAdmin && (
            <div className="hidden md:flex items-center justify-end gap-2 h-10 border-b border-gray-200 dark:border-gray-700">
              {userMenu}
            </div>
          )}

          <div className="flex items-center gap-3 min-h-[52px] py-1">
            <Link to="/" className="flex items-center flex-shrink-0">
              <img
                src={isDarkMode ? '/images/logo_dark.svg' : '/images/logo.svg'}
                alt="Stratosphere Logo"
                className="h-10 w-auto object-contain"
              />
            </Link>
            <div className="hidden md:block flex-1 min-w-0 overflow-x-auto overscroll-x-contain [-webkit-overflow-scrolling:touch] [scrollbar-width:thin]">
              <PolicyMakerPlatformNav />
            </div>
            <div className="flex items-center gap-2 shrink-0 pl-2 ml-auto md:ml-0">
              {onEditProfile && (
                <button
                  type="button"
                  onClick={onEditProfile}
                  className="hidden sm:flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 shrink-0"
                  title="Edit policy maker profile"
                >
                  <Settings className="h-4 w-4" />
                  <span>Settings</span>
                </button>
              )}
              <div className="md:hidden">{renderHamburgerButton()}</div>
              {!isSuperAdmin && <div className="hidden md:block">{userMenu}</div>}
            </div>
          </div>

          {isMobileMenuOpen && (
            <div className="md:hidden pb-4 border-t border-gray-200 dark:border-gray-700">
              <div className="pt-3 space-y-1 max-h-[75vh] overflow-y-auto">
                {mobilePlatformLinks.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={`platform-mobile-${item.path}`}
                      to={item.path}
                      onClick={closeMobileMenu}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${
                        isActive
                          ? 'bg-[#03396c]/10 dark:bg-blue-900/40 text-[#03396c] dark:text-blue-300'
                          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      <span>{item.label}</span>
                    </Link>
                  );
                })}
                {onEditProfile && (
                  <button
                    type="button"
                    onClick={() => {
                      closeMobileMenu();
                      onEditProfile();
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <Settings className="h-4 w-4" />
                    <span>Edit policy profile</span>
                  </button>
                )}
                {mobileUserActions}
              </div>
            </div>
          )}
        </div>
      </nav>
    );
  }

  return (
    <nav className="sticky top-0 z-50 bg-white dark:bg-gray-800 border-b border-secondary-200 dark:border-gray-700 transition-colors duration-200 w-full">
      <div className="w-full px-4">
        <div className="flex items-center justify-between gap-3 h-16 w-full min-w-0">
          <div className="flex items-center gap-3 min-w-0 flex-1 md:flex-initial md:max-w-none">
            <Link
              to={isEVDashboardMode ? '/ev-dashboard' : '/'}
              className="flex items-center flex-shrink-0"
            >
              <img
                src={isDarkMode ? '/images/logo_dark.svg' : '/images/logo.svg'}
                alt="Stratosphere Logo"
                className="h-12 w-auto object-contain"
              />
            </Link>
          </div>

          <div className={`hidden md:flex items-center space-x-1 flex-shrink-0 ${isMinimalNavPage ? 'ml-auto' : ''}`}>
            {!isMinimalNavPage && (isEVDashboardMode ? (
              // EV Dashboard Navigation
              visibleEvNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium ${
                      isActive
                        ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300'
                        : 'text-secondary-600 dark:text-gray-300'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{item.label}</span>
                  </Link>
                );
              })
            ) : (
              // Critical Minerals Dashboard Navigation
              <>
                {visibleNavItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;

                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium ${
                        isActive
                          ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300'
                          : 'text-secondary-600 dark:text-gray-300'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      <span>{item.label}</span>
                    </Link>
                  );
                })}

                {/* Intelligence Dropdown */}
                {visibleIntelligenceItems.length > 0 && (
                  <div className="relative" ref={intelligenceDropdownRef}>
                    <button
                      type="button"
                      onClick={() => {
                        setIsPredictionOpen(false);
                        setIsIntelligenceOpen(!isIntelligenceOpen);
                      }}
                      className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium ${
                        isIntelligenceActive
                          ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300'
                          : 'text-secondary-600 dark:text-gray-300 hover:text-secondary-900 dark:hover:text-white hover:bg-secondary-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      <INTELLIGENCE_DROPDOWN.icon className="h-4 w-4" />
                      <span>{INTELLIGENCE_DROPDOWN.label}</span>
                      <ChevronDown className={`h-3 w-3 transition-transform ${isIntelligenceOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isIntelligenceOpen && (
                      <div className="absolute top-full left-0 mt-1 min-w-[16rem] w-max max-w-[20rem] bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 py-1">
                        {visibleIntelligenceItems.map((item) => {
                          const Icon = item.icon;
                          const isActive = location.pathname === item.path;

                          return (
                            <Link
                              key={item.path}
                              to={item.path}
                              onClick={() => setIsIntelligenceOpen(false)}
                              className={`flex items-center space-x-3 px-4 py-3 text-sm font-medium ${
                                isActive
                                  ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300'
                                  : 'text-secondary-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                              }`}
                            >
                              <Icon className="h-4 w-4 flex-shrink-0" />
                              <span className="leading-snug">{item.label}</span>
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Prediction & Simulation Dropdown */}
                {visiblePredictionItems.length > 0 && (
                  <div className="relative" ref={predictionDropdownRef}>
                    <button
                      type="button"
                      onClick={() => {
                        setIsIntelligenceOpen(false);
                        setIsPredictionOpen(!isPredictionOpen);
                      }}
                      className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium ${
                        isPredictionActive
                          ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300'
                          : 'text-secondary-600 dark:text-gray-300 hover:text-secondary-900 dark:hover:text-white hover:bg-secondary-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      <PREDICTION_DROPDOWN.icon className="h-4 w-4" />
                      <span>{PREDICTION_DROPDOWN.label}</span>
                      <ChevronDown className={`h-3 w-3 transition-transform ${isPredictionOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isPredictionOpen && (
                      <div className="absolute top-full left-0 mt-1 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 py-1">
                        {visiblePredictionItems.map((item) => {
                            const Icon = item.icon;
                            const isActive = location.pathname === item.path;

                            return (
                              <Link
                                key={item.path}
                                to={item.path}
                                onClick={() => setIsPredictionOpen(false)}
                                className={`flex items-center space-x-3 px-4 py-3 text-sm font-medium ${
                                  isActive
                                    ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300'
                                    : 'text-secondary-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                                }`}
                              >
                                <Icon className="h-4 w-4" />
                                <span>{item.label}</span>
                              </Link>
                            );
                          })}
                      </div>
                    )}
                  </div>
                )}

              </>
            ))}

            {!isMinimalNavPage && <GlobalSearch />}

            {userMenu}
            
            {/* Settings Button */}
            {/* <Link
              to="/dashboard-settings"
              className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                location.pathname === '/dashboard-settings'
                  ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300'
                  : 'text-secondary-600 dark:text-gray-300 hover:text-secondary-900 dark:hover:text-white hover:bg-secondary-100 dark:hover:bg-gray-700'
              }`}
            >
              <Settings className="h-4 w-4" />
              <span>Settings</span>
            </Link> */}
          </div>
          
          <div className="flex md:hidden items-center gap-1 flex-shrink-0">
            {isMinimalNavPage ? (
              userMenu
            ) : (
              <>
                <GlobalSearch enableShortcut={false} />
                {renderHamburgerButton()}
              </>
            )}
          </div>
        </div>

        {isMobileMenuOpen && !isMinimalNavPage && (
          <div className="md:hidden pb-4 border-t border-secondary-200 dark:border-gray-700">
            <div className="pt-3 space-y-1">
              {(isEVDashboardMode ? visibleEvNavItems : visibleNavItems).map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={`mobile-${item.path}`}
                    to={item.path}
                    onClick={closeMobileMenu}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${
                      isActive
                        ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300'
                        : 'text-secondary-600 dark:text-gray-300 hover:bg-secondary-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}

              {!isEVDashboardMode && visibleIntelligenceItems.length > 0 && (
                <div className="pt-1">
                  <button
                    type="button"
                    onClick={() => setIsMobileIntelligenceOpen((prev) => !prev)}
                    className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium text-secondary-600 dark:text-gray-300 hover:bg-secondary-100 dark:hover:bg-gray-700"
                  >
                    <span className="flex items-center gap-2">
                      <INTELLIGENCE_DROPDOWN.icon className="h-4 w-4" />
                      {INTELLIGENCE_DROPDOWN.label}
                    </span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${isMobileIntelligenceOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isMobileIntelligenceOpen && (
                    <div className="mt-1 pl-3 space-y-1">
                      {visibleIntelligenceItems.map((item) => {
                        const Icon = item.icon;
                        const isActive = location.pathname === item.path;
                        return (
                          <Link
                            key={`mobile-intel-${item.path}`}
                            to={item.path}
                            onClick={closeMobileMenu}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                              isActive
                                ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300'
                                : 'text-secondary-600 dark:text-gray-300 hover:bg-secondary-100 dark:hover:bg-gray-700'
                            }`}
                          >
                            <Icon className="h-4 w-4" />
                            <span>{item.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {!isEVDashboardMode && visiblePredictionItems.length > 0 && (
                <div className="pt-1">
                  <button
                    type="button"
                    onClick={() => setIsMobilePredictionOpen((prev) => !prev)}
                    className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium text-secondary-600 dark:text-gray-300 hover:bg-secondary-100 dark:hover:bg-gray-700"
                  >
                    <span className="flex items-center gap-2">
                      <PREDICTION_DROPDOWN.icon className="h-4 w-4" />
                      {PREDICTION_DROPDOWN.label}
                    </span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${isMobilePredictionOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isMobilePredictionOpen && (
                    <div className="mt-1 pl-3 space-y-1">
                      {visiblePredictionItems.map((item) => {
                          const Icon = item.icon;
                          const isActive = location.pathname === item.path;
                          return (
                            <Link
                              key={`mobile-pred-${item.path}`}
                              to={item.path}
                              onClick={closeMobileMenu}
                              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                                isActive
                                  ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300'
                                  : 'text-secondary-600 dark:text-gray-300 hover:bg-secondary-100 dark:hover:bg-gray-700'
                              }`}
                            >
                              <Icon className="h-4 w-4" />
                              <span>{item.label}</span>
                            </Link>
                          );
                        })}
                    </div>
                  )}
                </div>
              )}

              {mobileUserActions}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar; 